import { Button, Flex, Heading, HStack } from "@chakra-ui/react";
import { useCallback } from "react";
import { Link as NavLink, useSearchParams } from "react-router-dom";
import {
  getSchoolSearchQuery,
  normalizeSearchQuery,
} from "src/components/graphql/utils";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { SortType, UNSORTED } from "src/components/Table/SortButton";
import { Glossary } from "src/components/Text/Glossary";
import { SearchAndFilterTypes } from "src/constants";
import { usePaginationParams } from "src/hooks/useCommonSearchParams";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { usePaginatedRemoteDataQuery } from "src/hooks/usePaginatedRemoteDataQuery";
import * as breadcrumb from "src/services/breadcrumb";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { BulkUploadSchools } from "./BulkUploadSchools";
import { ExportSchoolsButton } from "./ExportSchoolsButton";
import { GET_SCHOOLS } from "./graphql/queries";
import { SchoolsList } from "./List";

export function Schools() {
  const organization = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pagination, setPagination } = usePaginationParams();
  const { glossary } = useGlossary();

  const { usePaginatedQuery } = usePaginatedRemoteDataQuery<
    GQL.GetSchools,
    GQL.GetSchoolsVariables
  >(GET_SCHOOLS, {
    organization_id: organization.map((org) => org.id).withDefault(""),
    limit: pagination.limit,
    offset: pagination.offset,
    order_by: [{ name: GQL.order_by.asc }],
    search: searchParams.has(SearchAndFilterTypes.Search)
      ? getSchoolSearchQuery(
          searchParams.get(SearchAndFilterTypes.Search) ?? ""
        )
      : {},
  });
  const { remoteData, refetch } = usePaginatedQuery({
    skip: !organization.hasData(),
    notifyOnNetworkStatusChange: true,
  });

  const handleSort = useCallback(
    (columnName: string, sortType: SortType) => {
      if (sortType === UNSORTED) {
        refetch({ order_by: [{ [columnName]: null }] });
      } else {
        refetch({ order_by: [{ [columnName]: sortType }] });
      }
    },
    [refetch]
  );

  const handleChange = () => {
    refetch();
  };

  const handleSearch = useCallback(
    (searchTerm: string) => {
      const normalized = normalizeSearchQuery(searchTerm);
      if (!normalized) {
        searchParams.delete(SearchAndFilterTypes.Search);
      } else {
        searchParams.set(SearchAndFilterTypes.Search, normalized);
      }
      searchParams.delete(SearchAndFilterTypes.Offset);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleClearSearch = useCallback(() => handleSearch(""), [handleSearch]);

  return (
    <GQLRemoteDataView remoteData={remoteData}>
      {(data) => (
        <Flex direction="column" alignItems="flex-start" rowGap={6}>
          <Breadcrumb
            items={breadcrumb.school.getBreadcrumbsForList(organization)}
          />
          <HStack width="100%" justifyContent="space-between">
            <Heading as="h1" variant="admin">
              <Glossary>Schools</Glossary>
            </Heading>
            <HStack>
              <WithRequiredHasuraRoles roles={[HasuraRole.ADMIN]}>
                <BulkUploadSchools onComplete={handleChange} />
              </WithRequiredHasuraRoles>
              <ExportSchoolsButton />
              <WithUserPermissions permissions={["school:create"]}>
                <Button
                  as={NavLink}
                  to={organization
                    .map((org) => Url.OrgAdmin.Schools.new(org))
                    .withDefault("#")}
                  size="xl"
                >
                  <Glossary>Create school</Glossary>
                </Button>
              </WithUserPermissions>
            </HStack>
          </HStack>
          <HStack width="100%">
            <SearchInput
              onSearch={handleSearch}
              value={searchParams.get(SearchAndFilterTypes.Search) ?? ""}
              onClear={handleClearSearch}
              placeholder={glossary`Search by school name or ID`}
            />
          </HStack>
          <SchoolsList
            schools={data.school}
            onChange={handleChange}
            limit={pagination.limit}
            offset={pagination.offset}
            count={data.school_aggregate.totals?.total ?? 0}
            onFetchMore={setPagination}
            onSort={handleSort}
            onClearSearch={handleClearSearch}
          />
        </Flex>
      )}
    </GQLRemoteDataView>
  );
}
